import React from 'react';
import { getDefaultUnitSystem, formatWeight, formatLength } from '../utils/units';
import { parseWindowTime, WINDOW_TZ, torontoYMD } from '../utils/dbTime';
import { cardFace, faceLines } from '../utils/routeBuilderModel';
import DelayBadge from './DelayBadge';   // __DELAY_VIEW__ billable delay badge (office logins)

// __CARD_FACE__ (2026-10-16) the face reads like the Trello card: customer, then FROM -> TO (business names first).
// No load number on the face -- it is in the opened job (LoadDetailsModal) and the search still finds it.
const oneLine = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

function LoadCard({ load, isDragging }) {
  const unitSystem = getDefaultUnitSystem();

  const face = cardFace(load);

  // Earliest time window across all stops
  const allWindows = (load.load_stops || [])
    .filter(s => s.time_window_start)
    // __TZ_WINDOWS__ load_stops windows are Toronto wall clock (the hub writes them so):
    // parseWindowTime gives the real instant; parseDbTime read them as UTC (4-5 h early).
    .map(s => parseWindowTime(s.time_window_start))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const earliestWindow = allWindows[0];

  const formatTime = (dt) => dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: WINDOW_TZ });
  const formatDate = (dt) => {
    const isToday = torontoYMD(dt) === torontoYMD(new Date());
    return isToday ? `Today ${formatTime(dt)}` : `${dt.toLocaleDateString([], { month: 'short', day: 'numeric', timeZone: WINDOW_TZ })} ${formatTime(dt)}`;
  };

  return (
    <div style={{
      backgroundColor: isDragging ? 'var(--surface-2)' : 'var(--surface-1)',
      border: isDragging ? '2px solid var(--accent-primary)' : '1px solid var(--border-default)',
      borderRadius: 'var(--radius-md)',
      padding: '1rem',
      cursor: 'grab',
      transition: 'all 0.2s',
      boxShadow: isDragging ? '0 5px 15px rgba(35, 134, 54, 0.3)' : 'none'
    }}>

      {/* Customer + Time Window */}
      <div title={face.tooltip} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.3rem' }}>
        <div data-card-face="customer" style={{
          fontSize: '1rem', color: 'var(--text-primary)', fontWeight: 'bold', minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {face.customer}
        </div>
        {earliestWindow && (
          <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', fontWeight: '500', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {formatDate(earliestWindow)}
          </div>
        )}
        <DelayBadge loadId={load.id} style={{ flexShrink: 0 }} />
      </div>

      {/* From -> To: the To keeps its own line so a long From never pushes it off the card */}
      <div data-card-face="route" title={face.tooltip} aria-label={face.route} style={{ fontSize: '0.88rem', color: 'var(--text-primary)', lineHeight: 1.3, marginBottom: '0.35rem' }}>
        {faceLines(load).map((l, i) => <div key={i} style={oneLine}>{l}</div>)}
      </div>

      {/* Commodity */}
      <div style={{
        fontSize: '0.9rem',
        color: 'var(--text-secondary)',
        marginBottom: '0.5rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {load.commodity}
      </div>

      {/* Cargo Details */}
      {(load.weight_kg || load.length_cm) && (
        <div style={{
          display: 'flex',
          gap: '1rem',
          fontSize: '0.8rem',
          color: 'var(--text-secondary)',
          marginBottom: '0.5rem',
          paddingTop: '0.5rem',
          borderTop: '1px solid var(--border-default)'
        }}>
          {load.weight_kg && <div>{formatWeight(load.weight_kg, unitSystem)}</div>}
          {load.length_cm && (
            <div>
              {formatLength(load.length_cm, unitSystem)}
              x{formatLength(load.width_cm, unitSystem)}
              x{formatLength(load.height_cm, unitSystem)}
            </div>
          )}
        </div>
      )}

      {/* Driver & Truck */}
      <div style={{ fontSize: '0.8rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border-default)' }}>
        {load.driver ? (
          <div style={{ color: 'var(--color-success)' }}>
            {load.driver.user_id?.name || 'Driver assigned'}
          </div>
        ) : (
          <div style={{ color: 'var(--color-danger)' }}>No driver assigned</div>
        )}
        {load.truck ? (
          <div style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
            Truck {load.truck.truck_number}
          </div>
        ) : (
          <div style={{ color: 'var(--color-danger)', marginTop: '0.25rem' }}>No truck assigned</div>
        )}
      </div>
    </div>
  );
}

export default LoadCard;
